import { FC, ComponentProps } from 'react';
import { VStack } from '@bpba/ui-components';
import { RegisterOptions } from 'react-hook-form';
import { GenericInputError } from './GenericInputError';
import { StyledInput } from './StyledInput';
import { InputProps } from './InputProps';
import { INPUT_MAX_LENGTH_MESSAGE, INPUT_MIN_LENGTH_MESSAGE, INPUT_OBLIGATORY_MESSAGE } from '../../../constants/form';
import { usePlaceholder } from './usePlaceholder';
import { useInputAriaErrors } from './useInputAriaErrors';

type Props = {
  charactersType?: string;
  registerOptions?: RegisterOptions;
  maxLength?: number;
  minLength?: number;
  onBeforeInput?: React.FormEventHandler<HTMLInputElement> | undefined;
  placeholders?: string[] | undefined;
} & InputProps &
  ComponentProps<typeof StyledInput>;

export const GenericInput: FC<Props> = (props) => {
  const {
    name,
    required,
    charactersType,
    formRegister,
    registerOptions,
    errorCollection,
    maxLength,
    minLength,
    onBeforeInput,
    placeholders,
    mr,
    maxWidth,
    width,
    readOnly,
    ...otherProps
  } = props;

  const validationOptions = getValidationOptions(charactersType, required, registerOptions, maxLength, minLength);
  const validations = formRegister(name, validationOptions);
  const placeholder = usePlaceholder(placeholders!);
  const accesibleErrorsConfig = useInputAriaErrors(name, errorCollection, required);

  return (
    <VStack width={width} mr={mr} maxWidth={maxWidth}>
      <StyledInput
        mr={0}
        placeholder={placeholder}
        onBeforeInput={onBeforeInput}
        readOnly={readOnly}
        tabIndex={readOnly ? -1 : 0}
        {...accesibleErrorsConfig}
        {...otherProps}
        {...validations}
      />
      <GenericInputError fieldName={name} id={accesibleErrorsConfig['aria-describedby']} errorCollection={errorCollection} />
    </VStack>
  );
};

GenericInput.defaultProps = {
  required: false,
  charactersType: '',
  mr: '8px',
};

export function getValidationOptions(
  charactersType?: string,
  required?: boolean,
  currentOptions?: RegisterOptions,
  maxLength?: number,
  minLength?: number
): RegisterOptions {
  const registerOptions = currentOptions ? { ...currentOptions } : {};

  registerOptions.required = {
    value: !!required,
    message: INPUT_OBLIGATORY_MESSAGE,
  };

  if (maxLength !== undefined)
    registerOptions.maxLength = {
      value: maxLength,
      message: `${INPUT_MAX_LENGTH_MESSAGE} ${maxLength} ${charactersType}`,
    };

  if (minLength !== undefined)
    registerOptions.minLength = {
      value: minLength,
      message: `${INPUT_MIN_LENGTH_MESSAGE} ${minLength} ${charactersType}`,
    };

  return registerOptions;
}
